/**
 * @file LeaveTypesTab.tsx
 * @brief Manager settings tab for maintaining leave type enum_master values.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Plus, Trash2, Umbrella } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  enumMasterService,
  LEAVE_TYPE_KEY,
  LEAVE_TYPE_MODULE,
  type EnumMasterItem,
} from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

/**
 * LeaveTypesTab allows managers to add or deactivate leave types stored in enum_master.
 */
const LeaveTypesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [newLabel, setNewLabel] = useState('');
  const [confirmItem, setConfirmItem] = useState<EnumMasterItem | null>(null);

  const { data: leaveTypes = [], isLoading, error } = useQuery({
    queryKey: QUERY_KEYS.leaveTypes(true),
    queryFn: () => enumMasterService.listLeaveTypes(true),
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: (displayLabel: string) =>
      enumMasterService.create({
        moduleName: LEAVE_TYPE_MODULE,
        keyName: LEAVE_TYPE_KEY,
        displayLabel,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', LEAVE_TYPE_MODULE, LEAVE_TYPE_KEY] });
      setNewLabel('');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (enumId: string) => enumMasterService.deactivate(enumId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', LEAVE_TYPE_MODULE, LEAVE_TYPE_KEY] });
      setConfirmItem(null);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (enumId: string) => enumMasterService.update(enumId, { isActive: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', LEAVE_TYPE_MODULE, LEAVE_TYPE_KEY] });
    },
  });

  const activeTypes = leaveTypes.filter((item) => item.isActive);
  const inactiveTypes = leaveTypes.filter((item) => !item.isActive);

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    createMutation.mutate(label);
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Umbrella size={20} />
        <Typography variant="h6" fontWeight={600}>
          Leave Types
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Leave types are stored in enum_master and appear in the Request Leave dropdown for all users.
        Managers can add new types here.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Add Leave Type
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Leave type name"
              placeholder="e.g. Maternity Leave"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
            />
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={handleAdd}
              disabled={!newLabel.trim() || createMutation.isPending}
            >
              Add
            </Button>
          </Box>
          {createMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(createMutation.error as Error).message || 'Failed to add leave type'}
            </Alert>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Alert severity="error">Failed to load leave types.</Alert>
      ) : (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Active Leave Types ({activeTypes.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {activeTypes.map((item) => (
              <Chip
                key={item.id}
                label={item.displayLabel}
                onDelete={() => setConfirmItem(item)}
                deleteIcon={<Trash2 size={14} />}
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Box>

          {inactiveTypes.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Inactive ({inactiveTypes.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {inactiveTypes.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.displayLabel}
                    variant="outlined"
                    size="small"
                    onClick={() => reactivateMutation.mutate(item.id)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Click an inactive leave type to restore it.
              </Typography>
            </>
          )}
        </>
      )}

      <Dialog open={Boolean(confirmItem)} onClose={() => setConfirmItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Leave Type</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Deactivate &quot;{confirmItem?.displayLabel}&quot;? Existing leave requests keep their type,
            but it will no longer appear in the dropdown.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmItem(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmItem && deactivateMutation.mutate(confirmItem.id)}
            disabled={deactivateMutation.isPending}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveTypesTab;
